/**
 * Encode ATI string to binary representation
 */

import { parseATI } from './ariUtils';
import {
	ATI_PREFIX_MAP,
	BIT_SHIFTS,
	MAX_UNKNOWN_OWNERS,
	MAX_UNKNOWN_PREFIXES,
	MAX_UNKNOWN_TYPES,
	RESOURCE_OWNER_MAP,
	RESOURCE_TYPE_MAP,
} from './constants';
import type { AtiComponentKind, EncodedATI, UnknownComponents } from './types';

/**
 * Input for writeEncodedATI function
 */
export interface WriteEncodedATIInput {
	/** Target buffer to write to */
	buffer: Uint8Array;
	/** Encoded ATI to write to buffer */
	encoded: EncodedATI;
	/** Offset in buffer to start writing */
	offset: number;
}

/**
 * Configuration for component kind operations
 */
interface ComponentConfig {
	array: string[];
	errorMessage: string;
	maxLimit: number;
}

/**
 * Get configuration for a specific component kind
 */
function getComponentConfig(unknowns: UnknownComponents, kind: AtiComponentKind): ComponentConfig {
	switch (kind) {
		case 'prefix':
			return {
				array: unknowns.prefixes,
				maxLimit: MAX_UNKNOWN_PREFIXES,
				errorMessage: `Too many unknown ATI prefixes (max ${MAX_UNKNOWN_PREFIXES})`,
			};
		case 'owner':
			return {
				array: unknowns.owners,
				maxLimit: MAX_UNKNOWN_OWNERS,
				errorMessage: `Too many unknown resource owners (max ${MAX_UNKNOWN_OWNERS})`,
			};
		case 'type':
			return {
				array: unknowns.types,
				maxLimit: MAX_UNKNOWN_TYPES,
				errorMessage: `Too many unknown resource types (max ${MAX_UNKNOWN_TYPES})`,
			};
	}
}

/**
 * Ensure capacity for a component kind
 * @throws Error if capacity limit is exceeded
 */
function ensureCapacity(unknowns: UnknownComponents, kind: AtiComponentKind): void {
	const config = getComponentConfig(unknowns, kind);
	if (config.array.length >= config.maxLimit) {
		throw new Error(config.errorMessage);
	}
}

/**
 * Input for trackUnknown function
 */
interface TrackUnknownInput {
	/** Component kind */
	kind: AtiComponentKind;
	/** Accumulated unknown components */
	unknowns: UnknownComponents;
	/** Component value to track */
	value: string;
}

/**
 * Track unknown component value and return its index
 * Reuses existing index if value already tracked
 */
function trackUnknown({ unknowns, kind, value }: TrackUnknownInput): number {
	const config = getComponentConfig(unknowns, kind);
	const existing = config.array.indexOf(value);
	if (existing !== -1) {
		return existing;
	}

	ensureCapacity(unknowns, kind);
	config.array.push(value);
	return config.array.length - 1;
}

/**
 * Input for encodeComponent function
 */
interface EncodeComponentInput {
	/** Component kind */
	kind: AtiComponentKind;
	/** Map of known component values to codes */
	knownMap: Record<string, number>;
	/** Accumulated unknown components */
	unknowns: UnknownComponents;
	/** Component value to encode */
	value: string;
}

/**
 * Encode a component (prefix/owner/type) to its code
 * Returns code and unknown flag
 */
function encodeComponent({ unknowns, kind, value, knownMap }: EncodeComponentInput): {
	code: number;
	isUnknown: boolean;
} {
	const knownCode = knownMap[value];
	const isUnknown = knownCode === undefined;
	const code = isUnknown ? trackUnknown({ unknowns, kind, value }) : knownCode;
	return { code, isUnknown };
}

/**
 * Encode a single ATI string to its binary representation
 * Tracks unknown components for later query parameter encoding
 *
 * @private
 * @param ati - ATI string to encode
 * @param unknowns - Accumulated unknown components (mutated)
 * @returns Encoded ATI with component codes and flags
 * @throws Error if unknown component limits are exceeded
 */
export function ariStr2Bin(ati: string, unknowns: UnknownComponents): EncodedATI {
	const { prefix, owner, type } = parseATI(ati);

	const { code: prefixCode, isUnknown: prefixIsUnknown } = encodeComponent({
		unknowns,
		kind: 'prefix',
		value: prefix,
		knownMap: ATI_PREFIX_MAP,
	});

	const { code: ownerCode, isUnknown: ownerIsUnknown } = encodeComponent({
		unknowns,
		kind: 'owner',
		value: owner,
		knownMap: RESOURCE_OWNER_MAP,
	});

	const { code: typeCode, isUnknown: typeIsUnknown } = encodeComponent({
		unknowns,
		kind: 'type',
		value: type,
		knownMap: RESOURCE_TYPE_MAP,
	});

	return { prefixCode, prefixIsUnknown, ownerCode, ownerIsUnknown, typeCode, typeIsUnknown };
}

/**
 * Encode a single EncodedATI into a 3-byte buffer
 * Note: Big-Endian bit numbering (bit 7 = MSB, bit 0 = LSB)
 *
 * Binary format (3 bytes per ATI):
 *   Byte 0: [P|O|T|P|OOOO]
 *     P = Prefix flag (bit 7): 0=known, 1=unknown
 *     O = Owner flag (bit 6): 0=known, 1=unknown
 *     T = Type flag (bit 5): 0=known, 1=unknown
 *     P = Prefix value (bit 4): prefix code (0-1 for known, or unknown prefix index)
 *     OOOO = Owner upper 4 bits (bits 3-0)
 *   Byte 1: [OOOO|TTTT]
 *     OOOO = Owner lower 4 bits (bits 7-4)
 *     TTTT = Type upper 4 bits (bits 3-0)
 *   Byte 2: [TTTTTTTT]
 *     TTTTTTTT = Type lower 8 bits
 *
 * @param input - Input parameters
 * @param input.encoded - Encoded ATI to write to buffer
 * @param input.buffer - Target buffer to write to
 * @param input.offset - Offset in buffer to start writing
 */
export function writeEncodedATI({ encoded, buffer, offset }: WriteEncodedATIInput): void {
	const { prefixCode, prefixIsUnknown, ownerIsUnknown, typeIsUnknown, ownerCode, typeCode } =
		encoded;

	// Byte 0: [P|O|T|P|OOOO]
	const ownerUpper = (ownerCode >> BIT_SHIFTS.OWNER_UPPER_BITS) & 0x0f;
	const byte0 =
		((prefixIsUnknown ? 1 : 0) << BIT_SHIFTS.PREFIX_FLAG_SHIFT) |
		((ownerIsUnknown ? 1 : 0) << BIT_SHIFTS.OWNER_FLAG_SHIFT) |
		((typeIsUnknown ? 1 : 0) << BIT_SHIFTS.TYPE_FLAG_SHIFT) |
		((prefixCode & 0x01) << BIT_SHIFTS.PREFIX_VALUE_SHIFT) |
		ownerUpper;

	// Byte 1: [OOOO|TTTT]
	const ownerLower = ownerCode & 0x0f;
	const typeUpper = (typeCode >> 8) & 0x0f;
	const byte1 = (ownerLower << BIT_SHIFTS.OWNER_LOWER_SHIFT) | typeUpper;

	// Byte 2: [TTTTTTTT]
	const byte2 = typeCode & 0xff;

	// Write bytes directly (inline writeUInt8)
	buffer[offset] = byte0 & 0xff;
	buffer[offset + 1] = byte1 & 0xff;
	buffer[offset + 2] = byte2 & 0xff;
}
